"use client";
import React, { useMemo, useState } from "react";
import { Eye, X, Search, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import StatusChip from "./StatusChip";
import { useDashboard } from "../context/DashboardContext";
import { CDN_URL } from "../config/apiConfig";

// Client cell component
function ClientCell({ client }) {
  return (
    <div className="flex gap-3 items-center p-4">
      <div className="overflow-hidden relative w-10 h-10 rounded-full">
        {client.avatar ? (
          <img
            src={`${CDN_URL}/${client.avatar}`}
            alt=""
            className="object-cover w-full h-full"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-gray-200 rounded-full">
            <span className="text-xs text-gray-600">N/A</span>
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1">
        <div className="text-sm leading-snug text-gray-800">
          {client.name}
        </div>
        <div className="text-xs leading-tight text-gray-500">
          {client.company}
        </div>
      </div>
    </div>
  );
}

// Team members component
function TeamMembersCell({ members }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="flex gap-2 items-center p-4">
      <div className="flex -space-x-2">
        {members.slice(0, 4).map((member, index) => (
          <div
            key={index}
            className="relative"
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          >
            <img
            src={`${CDN_URL}/${member.image}`}
              // src={member.image}
              alt=""
              className="object-cover w-8 h-8 rounded-full border-2 border-white shadow-sm cursor-pointer hover:scale-110 transition-transform duration-200"
            />
            {hovered === index && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                {member.first_name} {member.last_name}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>
        ))}
        {members.length > 4 && (
          <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full border-2 border-white shadow-sm">
            <span className="text-xs font-semibold text-gray-700">
              +{members.length - 4}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientProjectTracker() {
  const { dashboardData, loading } = useDashboard();
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const filteredProjects = useMemo(() => {
    const list = dashboardData.projects_status || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((project) =>
      [project.project_name, project.first_name, project.company_name, project.project_type]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query))
    );
  }, [dashboardData.projects_status, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / itemsPerPage));
  const paginatedProjects = filteredProjects.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (loading) return <p>Loading...</p>;

  const handleView = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  return (
    <>
      <section className="overflow-hidden relative w-full rounded-2xl bg-white border border-gray-100 shadow-md">
        <header className="flex flex-wrap justify-between items-center gap-3 p-6 sm:p-8 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">
            Client-Project Tracker
          </h2>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 px-3 py-2 w-full sm:w-64">
              <Search className="text-gray-400 h-4 w-4 mr-2 shrink-0" />
              <input
                type="text"
                placeholder="Search client or project..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
              />
            </div>

            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1000px]">
            <thead className="bg-sky-50">
              <tr>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[200px]">Client Name</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[180px]">Project Title</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[150px]">Project Type</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[120px]">Start Date</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[120px]">Status</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[150px]">Team Members</th>
                <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[80px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.map((project) => (
                <tr key={project.id} className="border-t border-gray-50 hover:bg-gray-50 transition-colors duration-150">
                  <td className="p-0">
                    <ClientCell
                      client={{
                        name: project.first_name,
                        company: project.company_name,
                        avatar: project.client_image,
                      }}
                    />
                  </td>
                  <td className="p-4">{project.project_name}</td>
                  <td className="p-4">{project.project_type || "Unknown"}</td>
                  <td className="p-4">{project.start_date}</td>
                  <td className="p-4">
                    <StatusChip status={project.status} />
                  </td>
                  <td className="p-0">
                    <TeamMembersCell members={project.team_members} />
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleView(project)}
                      className="flex justify-center items-center p-2 rounded-lg cursor-pointer hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-all duration-200"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedProjects.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-400 text-sm">
                    No projects found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredProjects.length > itemsPerPage && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-4 sm:p-6 border-t border-gray-100 text-sm">
            <span className="text-sm text-gray-500 order-2 sm:order-1">
              Showing {(page - 1) * itemsPerPage + 1}-{Math.min(page * itemsPerPage, filteredProjects.length)} of {filteredProjects.length}
            </span>
            <div className="flex items-center flex-wrap justify-center gap-1 order-1 sm:order-2 w-full sm:w-auto">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowLeft size={16} />
              </button>

              {(() => {
                let startPage = Math.max(page - 1, 1);
                let endPage = startPage + 2;
                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(endPage - 2, 1);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === page
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <MdKeyboardArrowRight size={16} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Modal */}
      {showModal && selectedProject && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-xl">
      <div className="flex justify-between items-center p-6 border-b border-gray-100">
        <h3 className="text-xl font-bold text-gray-900">Project Details</h3>
        <button
          onClick={() => setShowModal(false)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <X size={20} />
        </button>
      </div>
      <div className="p-6 overflow-y-auto max-h-[70vh]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-lg font-semibold mb-4">Project Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Project Name</label>
                <p className="text-gray-900">{selectedProject.project_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Project Type</label>
                <p className="text-gray-900">{selectedProject.project_type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Start Date</label>
                <p className="text-gray-900">{selectedProject.start_date}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <StatusChip status={selectedProject.status} color={selectedProject.status_color} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Project Progress</label>
                <p className="text-gray-900">{selectedProject.progress_percentage}%</p>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Client Information</h4>
            <div className="flex items-center gap-3">
              {selectedProject.client_image ? (
                <img
                  src={`${CDN_URL}/${selectedProject.client_image}`}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-gray-500 text-sm">N/A</span>
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{selectedProject.first_name}</p>
                <p className="text-sm text-gray-500">{selectedProject.company_name}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-lg font-semibold mb-4">Team Members</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {selectedProject.team_members.map((member, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img
                  src={`${CDN_URL}/${member.image}`}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">{member.first_name} {member.last_name}</p>
                  <p className="text-xs text-gray-500">Team Member</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
)}

    </>
  );
}